import React, { useEffect } from "react";
import { useCallback, useImperativeHandle, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { IPrintOrder, PrintOrderType } from "./IPrintOrder";
import {
  createDefferedPromise,
  ICreateDefferedPromise,
} from "utils/DefferedPromise";
import { GetError } from "utils/GetError";
import { Resume } from "./resumoLayoutPrinter/Resume";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import ResizeObserver from "resize-observer-polyfill";
import { PrintType } from "domains/kiosk/aggregates/PrintType";
import { Ticket } from "./ticketLayoutPrinter/Ticket";
import CpfRegister from "./cpfRegisterLayoutPrinter/CpfRegister";
import { Remote } from "./remotePrinter/Remote";
import { Nfce } from "./invoiceLayoutPrinter/Nfce";
import { OfflineRecharge } from "./offlineRechargeLayout/OfflineRecharge";
import { WalletReceipt } from "./walletReceiptLayout/WalletReceipt";
import { WalletRecharge } from "./walletRechargeLayout/WalletRecharge";
import "./PrintLayouts.css";
// import debounce from "lodash/debounce";

export interface ILayoutPrinterProps {}

export interface ILayoutPrinterHandler {
  getPrintLayout(data: IPrintOrder): Promise<string>;
}

const LayoutPrinter: React.ForwardRefRenderFunction<
  ILayoutPrinterHandler,
  ILayoutPrinterProps
> = (_props, ref) => {
  const { kiosk } = useKiosk();
  const deffered = useRef<ICreateDefferedPromise<any>>(createDefferedPromise());

  const [printOrder, setPrintOrder] = useState<IPrintOrder | null>(null);
  const printOrderRef = useRef<IPrintOrder | null>(null); // Ref para acessar o printOrder atual em callbacks
  const layoutRef = useRef<HTMLDivElement | null>(null);
  
  // Atualiza a ref sempre que o printOrder muda
  useEffect(() => {
    printOrderRef.current = printOrder;
  }, [printOrder]);

  /**
   * Valida se o base64 gerado é válido
   * Verifica se não está vazio, se tem o formato correto e se não é apenas um placeholder
   */
  const isValidBase64 = useCallback((base64: string): boolean => {
    if (!base64 || base64.trim().length === 0) {
      console.warn("LayoutPrinter [isValidBase64]: Base64 está vazio");
      return false;
    }

    // Verifica se começa com o prefixo correto de data URL
    if (!base64.startsWith("data:image/")) {
      console.warn("LayoutPrinter [isValidBase64]: Base64 não é uma imagem válida");
      return false;
    }

    // Verifica se tem conteúdo suficiente (mais que apenas o header)
    // Um base64 válido de imagem PNG deve ter pelo menos alguns caracteres
    const base64Content = base64.split(",")[1];
    if (!base64Content || base64Content.length < 100) {
      console.warn("LayoutPrinter [isValidBase64]: Base64 muito curto, provavelmente inválido");
      return false;
    }

    // Verifica se não é apenas um placeholder ou imagem vazia
    // Imagens muito pequenas podem indicar que o canvas não foi renderizado corretamente
    // Um base64 de imagem PNG válida deve ter pelo menos 500 caracteres de conteúdo
    if (base64Content.length < 500) {
      console.warn("LayoutPrinter [isValidBase64]: Base64 muito pequeno, imagem pode estar vazia");
      return false;
    }

    return true;
  }, []);

  /**
   * Verifica se o elemento está pronto para ser capturado
   */
  const isElementReady = useCallback((element: HTMLDivElement): boolean => {
    // Verifica se o elemento tem conteúdo
    if (!element || element.children.length === 0) {
      console.warn("LayoutPrinter [isElementReady]: Elemento não tem filhos");
      return false;
    }

    // Verifica se o elemento tem dimensões válidas
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn("LayoutPrinter [isElementReady]: Elemento tem dimensões zero", rect);
      return false;
    }

    // Verifica se há imagens e se estão carregadas
    const images = element.querySelectorAll("img");
    for (let i = 0; i < images.length; i++) {
      const img = images[i] as HTMLImageElement;
      if (!img.complete) {
        console.warn("LayoutPrinter [isElementReady]: Imagem ainda não carregada", img.src);
        return false;
      }
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        console.warn("LayoutPrinter [isElementReady]: Imagem com dimensões inválidas", img.src);
        return false;
      }
    }

    return true;
  }, []);

  const capture = useCallback(async (retryCount: number = 0, maxRetries: number = 3): Promise<void> => {
    if (!layoutRef.current) {
      console.warn("LayoutPrinter: layoutRef.current is null");
      if (retryCount < maxRetries) {
        // Aguarda um pouco antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 200));
        return capture(retryCount + 1, maxRetries);
      }
      deffered.current?.rejecter(
        GetError("layoutRef.current is null após múltiplas tentativas", "capture", "LayoutPrinter")
      );
      return;
    }

    const element = layoutRef.current;

    // Verifica se o elemento está pronto
    if (!isElementReady(element)) {
      console.warn(`LayoutPrinter [capture]: Elemento não está pronto na tentativa ${retryCount + 1}`);
      if (retryCount < maxRetries) {
        // Aguarda mais tempo para imagens e conteúdo carregarem
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return capture(retryCount + 1, maxRetries);
      } else {
        deffered.current?.rejecter(
          GetError("Elemento não está pronto após múltiplas tentativas", "capture", "LayoutPrinter")
        );
        return;
      }
    }

    try {
      console.log(`LayoutPrinter [capture]: Starting capture (tentativa ${retryCount + 1}/${maxRetries + 1})`);
      
      // Log das dimensões do elemento
      const rect = element.getBoundingClientRect();
      console.log("LayoutPrinter [capture]: Dimensões do elemento", {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      });
      
      // Tenta capturar o canvas
      let canvas: HTMLCanvasElement;
      try {
        canvas = await html2canvas(element, {
          scale: 1,
          scrollY: 0,
          scrollX: 0,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: "#ffffff",
          width: rect.width || element.scrollWidth,
          height: rect.height || element.scrollHeight,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        });
      } catch (html2canvasError) {
        console.error("LayoutPrinter [capture]: Erro no html2canvas", html2canvasError);
        throw html2canvasError;
      }

      // Verifica se o canvas foi criado corretamente
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error(`Canvas inválido: width=${canvas?.width}, height=${canvas?.height}`);
      }

      console.log("LayoutPrinter [capture]: Canvas criado", {
        width: canvas.width,
        height: canvas.height
      });

      const base64image: string = canvas.toDataURL("image/png", 1);
      
      // Valida o base64 gerado
      if (!isValidBase64(base64image)) {
        console.warn(`LayoutPrinter [capture]: Base64 inválido na tentativa ${retryCount + 1}`, {
          length: base64image.length,
          startsWith: base64image.substring(0, 50)
        });
        
        if (retryCount < maxRetries) {
          // Aguarda um pouco mais antes de tentar novamente
          await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
          return capture(retryCount + 1, maxRetries);
        } else {
          throw new Error("Base64 inválido após múltiplas tentativas");
        }
      }

      console.log("[SUCCESS] LayoutPrinter: Image generated com base64 válido", {
        base64Length: base64image.length,
        canvasSize: `${canvas.width}x${canvas.height}`
      });
      
      // Resolve a promise ANTES de limpar o printOrder
      // Isso garante que a promise seja resolvida com o resultado correto
      deffered.current.resolver(base64image);
      setPrintOrder(null);
    } catch (error) {
      console.error(`LayoutPrinter [capture]: Erro na tentativa ${retryCount + 1}`, error);
      
      if (retryCount < maxRetries) {
        // Aguarda um pouco mais antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return capture(retryCount + 1, maxRetries);
      } else {
        deffered.current?.rejecter(GetError(error, "capture", "LayoutPrinter"));
      }
    }
  }, [isValidBase64, isElementReady]);

  const getPrintLayout = useCallback(async (_data: IPrintOrder) => {
    if (_data.type === PrintOrderType.PINPAD_RECEIPT) {
      return;
    }
    console.log("[LAYOUT_PRINTER] Nova impressão solicitada", _data);
    
    // Limpa o estado anterior antes de definir o novo printOrder
    // Isso garante que o React re-renderize o layout corretamente
    setPrintOrder(null);
    
    // Aguarda um frame para garantir que o estado foi limpo
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Define o novo printOrder
    setPrintOrder(_data);
    deffered.current = createDefferedPromise();
    return deffered.current.promise;
  }, []);

  useImperativeHandle(ref, () => ({ getPrintLayout }));

  const onLayout = useCallback(
    () => {
      // Usa a ref para verificar o printOrder atual (evita problemas de closure)
      const currentPrintOrder = printOrderRef.current;
      if (!currentPrintOrder) {
        console.warn("LayoutPrinter [onLayout]: printOrder é null, ignorando captura");
        return;
      }
      console.log("LayoutPrinter [onLayout]", new Date().toISOString(), currentPrintOrder);
      // Aguarda um pouco antes de capturar para garantir que o layout foi completamente renderizado
      setTimeout(() => {
        // Verifica novamente antes de capturar usando a ref (pode ter mudado durante o timeout)
        if (printOrderRef.current) {
          capture();
        } else {
          console.warn("LayoutPrinter [onLayout]: printOrder foi limpo durante o timeout, cancelando captura");
        }
      }, 300);
    },
    [capture]
  );

  useEffect(() => {
    const element = layoutRef.current;
    const currentPrintOrder = printOrderRef.current; // Usa a ref para evitar problemas de closure
    if (!element || !currentPrintOrder) {
      // Se não há printOrder, não precisa observar
      return;
    }

    let observer: ResizeObserver | null = null;
    let initialCaptureTimeout: NodeJS.Timeout | null = null;
    let hasCaptured = false; // Flag para evitar múltiplas capturas do mesmo printOrder
    const printOrderId = currentPrintOrder.id; // Salva o ID para verificar se ainda é o mesmo printOrder

    // Aguarda um pouco antes de observar para garantir que o elemento está no DOM
    const timeoutId = setTimeout(() => {
      observer = new ResizeObserver((entries) => {
        // Verifica se ainda é o mesmo printOrder usando a ref
        const currentOrder = printOrderRef.current;
        if (!currentOrder || currentOrder.id !== printOrderId) {
          console.warn("LayoutPrinter [ResizeObserver]: printOrder mudou, ignorando");
          return;
        }
        
        // Verifica se o elemento tem dimensões válidas e se há um printOrder válido antes de tentar capturar
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0 && !hasCaptured) {
            hasCaptured = true; // Marca que já tentou capturar para este printOrder
            onLayout();
          } else if (hasCaptured) {
            console.warn("LayoutPrinter [ResizeObserver]: Já capturado para este printOrder, ignorando");
          } else {
            console.warn("LayoutPrinter [ResizeObserver]: Elemento ainda não tem dimensões válidas", { width, height });
          }
        }
      });

      observer.observe(element);

      // Também tenta capturar após um delay inicial caso o ResizeObserver não dispare
      initialCaptureTimeout = setTimeout(() => {
        const currentOrder = printOrderRef.current;
        if (element && element.children.length > 0 && currentOrder && currentOrder.id === printOrderId && !hasCaptured) {
          console.log("LayoutPrinter: Tentando captura inicial após delay");
          hasCaptured = true;
          onLayout();
        }
      }, 1000);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (initialCaptureTimeout) {
        clearTimeout(initialCaptureTimeout);
      }
      if (observer) {
        observer.disconnect();
      }
      hasCaptured = false; // Reset da flag quando o efeito é limpo
    };
  }, [onLayout, printOrder]);

  const renderLayout = () => {
    if (!printOrder || !kiosk) return null;

    switch (printOrder.type) {
      case PrintOrderType.ORDER:
        switch (printOrder.printType) {
          case PrintType.RESUME:
            return <Resume kiosk={kiosk} order={printOrder.data}/>;
          case PrintType.GROUPED_RESUME:
            return <></>;
          case PrintType.TICKET:
          case PrintType.SIMPLE_RESUME:
            return <Ticket kiosk={kiosk} order={printOrder.data}></Ticket>;
          default:
            return null;
        }
      case PrintOrderType.CPF_REGISTER:
        return <CpfRegister order={printOrder.data} />;
      case PrintOrderType.REMOTE_PRINT:
        return <Remote kiosk={kiosk} order={printOrder.data} />;
      case PrintOrderType.NFCE:
        return <Nfce kiosk={kiosk} order={printOrder.data}/>;
      case PrintOrderType.OFFLINE_RECHARGE:
        return <OfflineRecharge kiosk={kiosk} order={printOrder.data}/>;
      case PrintOrderType.WALLET_RECEIPT:
        return <WalletReceipt kiosk={kiosk} order={printOrder.data}/>;
      case PrintOrderType.WALLET_RECHARGE:
        return <WalletRecharge kiosk={kiosk} order={printOrder.data}/>;
      default:
        return null;
    }
  };

  return (
    <div
      ref={layoutRef}
      key={printOrder?.id || "empty"} // Key para forçar re-renderização quando o printOrder muda
      style={{
        bottom: -1000,
        left: -50000,
        zIndex: -5000000,
        position: "fixed",
        overflow: "visible",
        minWidth: 300,
      }}
    >
      {renderLayout()}
    </div>
  );
};

export default React.forwardRef(LayoutPrinter);
